import React from "react";

type Dict = Record<string, unknown>;

const asDict = (value: unknown): Dict =>
	value && typeof value === "object" && !Array.isArray(value)
		? (value as Dict)
		: {};

const asArray = (value: unknown): unknown[] => (Array.isArray(value) ? value : []);

const text = (value: unknown, fallback = ""): string =>
	value === null || value === undefined ? fallback : String(value);

const count = (value: unknown): number =>
	typeof value === "number" && Number.isFinite(value) ? value : 0;

const statusClass = (status: unknown): string => {
	const s = text(status).toLowerCase().replace(/_/g, "-");
	if (["verified", "covered", "selected", "corroborated"].includes(s)) {
		return "bg-emerald-50 text-emerald-700 border-emerald-200";
	}
	if (["conflict", "invalid", "failed"].includes(s)) {
		return "bg-red-50 text-red-700 border-red-200";
	}
	if (["unverified", "ambiguous", "uncited"].includes(s)) {
		return "bg-amber-50 text-amber-800 border-amber-200";
	}
	return "bg-gray-100 text-gray-700 border-gray-200";
};

const Badge: React.FC<{ value: unknown }> = ({ value }) => (
	<span
		className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${statusClass(
			value,
		)}`}
	>
		{text(value, "unknown")}
	</span>
);

const CounterPills: React.FC<{ value: unknown }> = ({ value }) => {
	const entries = Object.entries(asDict(value)).filter(([, v]) => count(v) !== 0);
	if (entries.length === 0) {
		return <span className="text-xs text-gray-500">none</span>;
	}
	return (
		<div className="flex flex-wrap gap-1.5">
			{entries.map(([key, val]) => (
				<span
					key={key}
					className="inline-flex items-center gap-1 rounded-full border border-gray-200 bg-white px-2 py-0.5 text-xs text-gray-700"
				>
					<strong>{key}</strong>
					{count(val)}
				</span>
			))}
		</div>
	);
};

const sourceLabel = (source: unknown): string => {
	const src = asDict(source);
	const kind = text(src.kind, "unknown");
	const filename = text(src.filename || src.file_id, kind);
	if (kind === "excel") {
		return `${filename} / ${text(src.sheet)}!${text(src.ref)}`.replace(
			/\s*\/\s*!?$/,
			"",
		);
	}
	if (kind === "pdf") {
		const page = src.page ? ` page ${text(src.page)}` : "";
		const excerpt = src.excerpt ? ` - ${text(src.excerpt)}` : "";
		return `${filename}${page}${excerpt}`;
	}
	if (kind === "derived") {
		return `derived ${text(src.operation)} (${count(src.input_count)} inputs)`;
	}
	return filename;
};

const RuntimeModeCard: React.FC<{ mode: unknown }> = ({ mode }) => {
	const data = asDict(mode);
	const markdown = asDict(data.markdown);
	const artifact = asDict(data.artifact);
	const coverage = asDict(artifact.coverage);
	return (
		<div className="rounded-lg border border-gray-200 bg-white p-3">
			<div className="mb-3 flex items-center justify-between gap-2">
				<div className="text-sm font-semibold text-gray-900">
					{text(data.mode, "mode")}
				</div>
				<Badge value={coverage.status || (artifact.built ? "built" : "no artifact")} />
			</div>
			<div className="grid grid-cols-2 gap-2 text-xs">
				<div className="rounded-md border border-gray-200 bg-gray-50 p-2">
					<div className="text-gray-500">Values</div>
					<div className="text-lg font-semibold text-gray-900">
						{count(markdown.answer_value_cells)}
					</div>
				</div>
				<div className="rounded-md border border-gray-200 bg-gray-50 p-2">
					<div className="text-gray-500">Links</div>
					<div className="text-lg font-semibold text-gray-900">
						{count(markdown.doppler_link_count)}
					</div>
				</div>
				<div className="rounded-md border border-gray-200 bg-gray-50 p-2">
					<div className="text-gray-500">Raw Locators</div>
					<div className="text-lg font-semibold text-gray-900">
						{count(markdown.raw_locator_count)}
					</div>
				</div>
				<div className="rounded-md border border-gray-200 bg-gray-50 p-2">
					<div className="text-gray-500">Bare Estimate</div>
					<div className="text-lg font-semibold text-gray-900">
						{count(markdown.bare_value_estimate)}
					</div>
				</div>
			</div>
			<div className="mt-3 text-xs text-gray-600">
				Artifact: {artifact.built ? "built" : "not built"} · layout{" "}
				{text(artifact.layout, "n/a")} · cells {count(artifact.cell_count)}
			</div>
			<div className="mt-2">
				<CounterPills value={artifact.verification_counts} />
			</div>
		</div>
	);
};

const SlotRows: React.FC<{ slots: unknown[] }> = ({ slots }) => {
	if (slots.length === 0) {
		return (
			<tr>
				<td className="px-3 py-3 text-sm text-gray-500" colSpan={6}>
					No slots.
				</td>
			</tr>
		);
	}
	return (
		<>
			{slots.map((slot, idx) => {
				const row = asDict(slot);
				const resolution = asDict(row.resolution);
				const candidates = asArray(row.candidates).map(asDict);
				return (
					<tr key={`${text(row.slot_key)}-${idx}`}>
						<td className="whitespace-nowrap px-3 py-2 align-top">
							<Badge value={resolution.status} />
						</td>
						<td className="px-3 py-2 align-top">
							<code className="text-xs">{text(row.metric_key)}</code>
						</td>
						<td className="px-3 py-2 align-top">
							<code className="text-xs">{text(row.period_key)}</code>
						</td>
						<td className="px-3 py-2 align-top text-sm text-gray-700">
							{count(row.candidate_count)}
						</td>
						<td className="px-3 py-2 align-top">
							<div className="space-y-2">
								{candidates.map((candidate) => (
									<div key={text(candidate.atom_id)} className="text-xs">
										<div>
											<strong>{text(candidate.display_value)}</strong>{" "}
											<span className="text-gray-500">
												{text(candidate.atom_id)}
											</span>
										</div>
										<div className="text-gray-600">
											{sourceLabel(candidate.source)}
										</div>
									</div>
								))}
							</div>
						</td>
						<td className="px-3 py-2 align-top">
							<code className="text-xs">{text(resolution.selected_atom_id)}</code>
						</td>
					</tr>
				);
			})}
		</>
	);
};

const RejectedAtoms: React.FC<{ atoms: unknown[] }> = ({ atoms }) => {
	if (atoms.length === 0) return null;
	return (
		<details className="mt-3 rounded-lg border border-gray-200 bg-gray-50 p-3">
			<summary className="cursor-pointer text-sm font-medium text-gray-800">
				Rejected atoms ({atoms.length})
			</summary>
			<div className="mt-3 space-y-2">
				{atoms.map((atom, idx) => {
					const row = asDict(atom);
					return (
						<div
							key={`${text(row.atom_id)}-${idx}`}
							className="rounded-md border border-gray-200 bg-white p-2 text-xs"
						>
							<div className="mb-1 flex items-center gap-2">
								<Badge value={row.reject_reason} />
								<code>{text(row.atom_id)}</code>
								<strong>{text(row.display_value)}</strong>
							</div>
							<div className="text-gray-600">{sourceLabel(row.source)}</div>
						</div>
					);
				})}
			</div>
		</details>
	);
};

const CitationHarnessReportView: React.FC<{
	report: Record<string, unknown>;
	title?: string;
}> = ({ report, title = "Citation Harness" }) => {
	const input = asDict(report.input);
	const config = asDict(report.config);
	const ledger = asDict(report.atom_slot_ledger);
	const runtimeModes = asArray(report.runtime_modes);
	const slots = asArray(ledger.slots);
	return (
		<div className="not-prose my-3 w-full max-w-[832px] rounded-lg border border-gray-200 bg-white p-4 text-gray-900">
			<div className="mb-3 flex flex-wrap items-start justify-between gap-3">
				<div>
					<div className="text-sm font-semibold">{title}</div>
					<div className="text-xs text-gray-500">
						{count(input.read_citation_count)} citations · layout{" "}
						{text(config.citation_table_layout, "auto")} · slots{" "}
						{text(asArray(config.slot_dimensions).join(", "), "metric, period")}
					</div>
				</div>
				<CounterPills value={ledger.slot_status_counts} />
			</div>

			<div className="grid grid-cols-1 gap-3 md:grid-cols-2">
				{runtimeModes.map((mode, idx) => (
					<RuntimeModeCard key={`${text(asDict(mode).mode)}-${idx}`} mode={mode} />
				))}
			</div>

			<div className="mt-3 overflow-x-auto rounded-lg border border-gray-200">
				<table className="w-full border-collapse text-left">
					<thead className="bg-gray-50 text-xs text-gray-500">
						<tr>
							<th className="px-3 py-2 font-medium">Status</th>
							<th className="px-3 py-2 font-medium">Metric</th>
							<th className="px-3 py-2 font-medium">Period</th>
							<th className="px-3 py-2 font-medium">Candidates</th>
							<th className="px-3 py-2 font-medium">Sources</th>
							<th className="px-3 py-2 font-medium">Selected</th>
						</tr>
					</thead>
					<tbody className="divide-y divide-gray-200 bg-white">
						<SlotRows slots={slots} />
					</tbody>
				</table>
			</div>

			<RejectedAtoms atoms={asArray(ledger.rejected_atoms)} />

			{input.verification_note ? (
				<div className="mt-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
					{text(input.verification_note)}
				</div>
			) : null}
		</div>
	);
};

export default CitationHarnessReportView;
